import { Card, CardContent, CardHeader, Dialog, TextField } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import lDbgImage from '../assets/bg_login.jpg';
import { LogoIcon } from '../LogoIcon';
import { transl } from '../main/transl';
import { commSvc } from '../services/CommSvc';
import { toasterSvc } from '../services/ToasterSvc';

const styles = (theme: Theme) => createStyles({
	rootDialog: {
		fontFamily: '"TT Hoves", "Roboto", "Helvetica", "Arial", sans-serif',
		letterSpacing: 'normal',
		'& .MuiTypography-root': {
			fontFamily: 'inherit',
			letterSpacing: 'inherit'
		},
	},
	root: {
		'& .MuiBackdrop-root': {
			backgroundColor: 'rgba(0, 0, 0, 0)'
		},
		flexDirection: 'column',
		flexWrap: 'wrap',
		justifyContent: 'center',
		position: 'relative',
		'& .MuiDialog-container': {
			backgroundColor: '#fff',
			backgroundImage: `url(${lDbgImage})`,
			height: '100%',
			backgroundSize: 'cover',

		},
		'& .MuiPaper-root.MuiDialog-paper': {
			backgroundColor: 'transparent'
		}
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		width: '100%',
	},
	MuiCardHeader_title: {
		color: 'rgba(0, 0, 0, 1)',
		lineHeight: '2',
		fontWeight: 'bold',
		fontFamily: 'inherit',
		letterSpacing: 'inherit'
	},
	MuiCardHeader_subheader: {
		color: 'rgba(0, 0, 0, 0.87)',
		fontSize: '1.1rem',
		lineHeight: 'normal',
		fontFamily: 'inherit',
		letterSpacing: 'inherit'
	},
	card: {
		flex: 1,
		width: '320px',
		margin: '20px auto',
		borderRadius: '10px',
		backgroundColor: '#fff'
	},
	cardHeader: {
		padding: '25px 30px',
		paddingBottom: '0px',
	},
	cardContent: {
		marginTop: '16px',
		padding: '30px',
		paddingTop: '0px',
		paddingBottom: '10px !important'
	},
	textField: {
		margin: '5px 0',
		width: '100%',
		'& .MuiInputBase-root': {
			borderRadius: '8px',
			border: '0px solid #f7f7f7',
			backgroundColor: '#f7f7f7',
		},
		'& .MuiFilledInput-underline:before': {
			borderBottom: '0px',
			borderRadius: '8px'
		},
		'& .MuiFilledInput-underline:hover:before': {
			borderBottom: '0px'
		},
		'& .MuiFilledInput-input': {
			padding: '10px 15px 8px'
		},
		'& .MuiFilledInput-underline:after': {
			borderBottom: '0px',
			borderRadius: '8px'
		},
		'& .Mui-error': {
			border: '0px solid  #b9747a',
			boxShadow: '0px 0px 2px 1.5px #b9747a'
		},
		'& .MuiInputBase-root.Mui-focused': {
			border: '0px solid #eaeaea',
			backgroundColor: '#eaeaea',
			boxShadow: 'none'
		},
	},
	button: {
		margin: '20px auto',
		backgroundColor: '#5b49dd',
		fontWeight: 'bold',
		padding: '5px 25px',
		'&.MuiButton-containedPrimary:hover': {
			backgroundColor: 'rgba(205,0,88,0.87)'
		}
	},
	logoContainer: {
		margin: '20px auto',
		textAlign: 'center'
	},
	logo: {
		display: 'inline-block',
		height: '16em',
		[theme.breakpoints.down('xs')]: {
			height: '12.5em',
		},
	},
});

interface ISetPasswordDialogProps {
	locationStore: {secret: string, clearSecret: () => void}
	classes: any
}

function _SetPasswordDialog({ locationStore, classes }: ISetPasswordDialogProps) {
	const store = useLocalStore(() => ({
		password: '',
		passwordRep: '',
		get error() : string {
			if (!store.password) return 'emptyPassword';
			if (store.password !== store.passwordRep) return 'mismatchedPassword';
			return '';
		},
		onChange(e: React.ChangeEvent<HTMLInputElement>) {
			runInAction(function() {
				store[e.target.name] = e.target.value;
			});
		},
		close() {
			locationStore.clearSecret();
		},
		accept() {
			if (store.error) {
				toasterSvc.error(store.error);
				return;
			}
			commSvc.doPost('SetPassword', {secret: locationStore.secret, password: store.password})
				.then(toasterSvc.failure)
				.then((r) => r.outputList && toasterSvc.success('setPasswordSuccess'))
				.then(() => locationStore.clearSecret());
		},
	}));

	if (!locationStore.secret) return null;

	return <Dialog open={true} className={classes.root} onClose={store.close} fullScreen>
		<div className={classes.rootDialog} >
			<div className={classes.logoContainer}><LogoIcon className={classes.logo} /></div>
			<Card className={classes.card}>
				<CardHeader className={classes.cardHeader} title={transl('titleSetPassword')} subheader={transl('subHeadingSetPassword')}
					classes={{ title: classes.MuiCardHeader_title, subheader: classes.MuiCardHeader_subheader }}
				/>
				<CardContent className={classes.cardContent}>
					{
						['password', 'passwordRep'].map(s => <TextField
							required
							key={s}
							type="password"
							className={classes.textField}
							name={s}
							placeholder={transl(s)}
							value={store[s]}
							onChange={store.onChange}
							variant="filled"
						/>)
					}
					<Button variant="contained" color="primary" className={classes.button} onClick={store.accept}>
						{transl('setPassword')}
					</Button>
				</CardContent>
			</Card>
		</div>
	</Dialog >;
}

export const SetPasswordDialog = withStyles(styles, { name: 'SetPasswordDialog' })(observer(_SetPasswordDialog));
